import { useState, useCallback, useRef, useEffect } from 'react';
import { RATE_LIMIT_CONFIG } from '../utils/security';

interface RateLimitState {
  isLimited: boolean;
  remainingAttempts: number;
  resetTime: number;
}

interface UseRateLimitOptions {
  limit: number;
  windowMs: number;
  key?: string;
}

interface UseRateLimitReturn {
  isLimited: boolean;
  remainingAttempts: number;
  resetTime: number;
  checkLimit: () => boolean;
  recordAttempt: () => void;
  reset: () => void;
}

// In-memory storage for rate limiting (in production, use Redis or similar)
const rateLimitStore = new Map<string, { count: number; resetTime: number }>();

export function useRateLimit({
  limit,
  windowMs,
  key = 'default',
}: UseRateLimitOptions): UseRateLimitReturn {
  const [state, setState] = useState<RateLimitState>({
    isLimited: false,
    remainingAttempts: limit,
    resetTime: 0,
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const checkLimit = useCallback((): boolean => {
    const now = Date.now();
    const storeKey = `${key}_${Math.floor(now / windowMs)}`;
    const current = rateLimitStore.get(storeKey) || { count: 0, resetTime: now + windowMs };

    const isLimited = current.count >= limit;
    const remainingAttempts = Math.max(0, limit - current.count);
    const resetTime = current.resetTime;

    setState({
      isLimited,
      remainingAttempts,
      resetTime,
    });

    return !isLimited;
  }, [limit, windowMs, key]);

  const recordAttempt = useCallback(() => {
    const now = Date.now();
    const storeKey = `${key}_${Math.floor(now / windowMs)}`;
    const current = rateLimitStore.get(storeKey) || { count: 0, resetTime: now + windowMs };

    current.count += 1;
    rateLimitStore.set(storeKey, current);

    checkLimit();
  }, [key, windowMs, checkLimit]);

  const reset = useCallback(() => {
    const now = Date.now();
    const storeKey = `${key}_${Math.floor(now / windowMs)}`;
    rateLimitStore.delete(storeKey);

    setState({
      isLimited: false,
      remainingAttempts: limit,
      resetTime: 0,
    });
  }, [key, windowMs, limit]);

  // Clean up old entries periodically
  useEffect(() => {
    const cleanup = () => {
      const now = Date.now();
      const entries = Array.from(rateLimitStore.entries());
      for (const [storeKey, data] of entries) {
        if (now > data.resetTime) {
          rateLimitStore.delete(storeKey);
        }
      }
    };

    intervalRef.current = setInterval(cleanup, windowMs);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [windowMs]);

  // Check limit on mount
  useEffect(() => {
    checkLimit();
  }, [checkLimit]);

  return {
    ...state,
    checkLimit,
    recordAttempt,
    reset,
  };
}

// Pre-configured rate limit hooks for common use cases
export function useApiRateLimit(operation: keyof typeof RATE_LIMIT_CONFIG.api) {
  return useRateLimit({
    limit: RATE_LIMIT_CONFIG.api[operation],
    windowMs: 60 * 1000, // 1 minute
    key: `api_${operation}`,
  });
}

export function useUIRateLimit(operation: keyof typeof RATE_LIMIT_CONFIG.ui) {
  return useRateLimit({
    limit: RATE_LIMIT_CONFIG.ui[operation],
    windowMs: 60 * 1000, // 1 minute
    key: `ui_${operation}`,
  });
}

// Hook for form submission rate limiting
export function useFormSubmissionRateLimit() {
  return useUIRateLimit('formSubmit');
}

// Hook for file upload rate limiting
export function useFileUploadRateLimit() {
  return useUIRateLimit('fileUpload');
}

// Hook for search rate limiting
export function useSearchRateLimit() {
  return useUIRateLimit('search');
}

// Hook for login rate limiting
export function useLoginRateLimit() {
  return useApiRateLimit('login');
}

// Hook for password reset rate limiting
export function usePasswordResetRateLimit() {
  return useApiRateLimit('passwordReset');
}
